import React from 'react';
import { Grid, Paper, Stack, Chip, Typography, Divider, Button, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import FilterDropdown from './FilterDropdown';
import { COLOR_GREY_600, COLOR_PRIMARY_2, COLOR_PRIMARY_2_800 } from 'src/utils/colors';
import DownloadIcon from '@mui/icons-material/Download';
import { formatCurrency } from 'src/utils/functions';
import { COLOR_PRIMARY, exchangeRates } from 'src/utils/const';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';

// Define the shape of options used in filters
interface FilterOption {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
  icon?: any;
  tooltip?: string;
}

// Define common button styles (can be shared or defined locally)
const commonButtonSx = {
  justifyContent: 'flex-start',
  boxShadow: 'none',
  height: '36.5px', // Match typical Button height
  minWidth: 'auto', // Adjust as needed
};
const outlinedButtonSx = {
  borderColor: 'divider', // Use divider color for subtle border
  color: 'text.primary',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'action.hover',
    borderColor: 'divider',
  },
};

// Define the props for the SelectedFiltersDisplay component - UPDATED
interface SelectedFiltersDisplayProps {
  selectedRegions: string[];
  regionOptions: FilterOption[];
  handleRemoveRegion: (value: string) => void;
  selectedLocations: string[];
  selectedMarketSizes: string[];
  selectedSortBy: string;
  countryOptions: FilterOption[];
  marketSizeOptions: FilterOption[];
  sortByOptions: FilterOption[];
  findLabel: (options: FilterOption[], value: string) => string;
  handleRemoveLocation: (value: string) => void;
  handleRemoveMarketSize: (value: string) => void;
  handleSortByChange: (value: string | string[]) => void;
  isMobileScreen: boolean;
  isFreeUser: boolean;
  handleOnSampleClick: (plan: string) => void;
  currency: string;
  userPickedColumnFields: string[];
}

const SelectedFiltersDisplay: React.FC<SelectedFiltersDisplayProps> = ({
  selectedRegions,
  regionOptions,
  handleRemoveRegion,
  selectedLocations,
  selectedMarketSizes,
  selectedSortBy,
  countryOptions,
  marketSizeOptions,
  sortByOptions,
  findLabel,
  handleRemoveLocation,
  handleRemoveMarketSize,
  handleSortByChange,
  isMobileScreen,
  isFreeUser,
  handleOnSampleClick,
  currency,
  userPickedColumnFields,
}) => {
  const hasRegions = selectedRegions.length > 0;
  const hasLocations = selectedLocations.length > 0;
  const hasMarketSizes = selectedMarketSizes.length > 0;
  const hasActiveFilters = hasLocations || hasMarketSizes;

  const selectedSortByLabel = findLabel(sortByOptions, selectedSortBy) || 'Sort By';
  const hasAnyActiveContent = hasActiveFilters || selectedSortBy;

  if (!hasAnyActiveContent) {
    return null; // Don't render if nothing is selected/sorted
  }

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%',
          mb: 1,
        }}
      >
        {/* Display Individual Filter Chips */}
        {hasActiveFilters && (
          <Typography
            variant="body2"
            sx={{ fontWeight: 'medium', color: 'text.secondary', mr: 1, flexShrink: 0 }}
          >
            Filters:
          </Typography>
        )}
        {/* Region chips before location */}
        {selectedRegions.map(value => (
          <Chip
            key={`region-${value}`}
            label={findLabel(regionOptions, value)}
            size="small"
            onDelete={() => handleRemoveRegion(value)}
          />
        ))}
        {selectedLocations.map(value => (
          <Chip
            key={`loc-${value}`}
            label={findLabel(countryOptions, value)}
            size="small"
            onDelete={() => handleRemoveLocation(value)}
          />
        ))}
        {selectedMarketSizes.map(value => (
          <Chip
            key={`size-${value}`}
            label={findLabel(marketSizeOptions, value)}
            size="small"
            onDelete={() => handleRemoveMarketSize(value)}
          />
        ))}
      </Box>

      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          position: 'sticky',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* Sort By Chips */}
        <Stack direction="row" gap={1}>
          {sortByOptions.map(option => (
            <TooltipNew key={option.value} title={''} content={option.tooltip || ''}>
              <Chip
                key={option.value + 'chip'}
                label={option.label}
                clickable
                color="primary"
                variant={selectedSortBy === option.value ? 'filled' : 'outlined'}
                onClick={() => handleSortByChange(option.value)}
                icon={option.icon}
                sx={{
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  height: 28,
                  minWidth: 0,
                  ...(selectedSortBy === option.value
                    ? {
                        px: '4px',
                        backgroundColor: COLOR_PRIMARY_2_800, // COLOR_PRIMARY_2_600
                        color: '#fff',
                        boxShadow: '0 1px 4px 0 rgba(4, 50, 80, 0.10)',
                      }
                    : {
                        px: '0px',
                        mx: 0,
                        backgroundColor: 'transparent',
                        color: COLOR_GREY_600,
                        border: 'none',
                        boxShadow: 'none',
                        '& .MuiChip-label': {
                          paddingRight: '2px !important',
                        },
                      }),
                }}
              />
            </TooltipNew>
          ))}
        </Stack>
      </Box>
    </Grid>
  );
};

export default SelectedFiltersDisplay;
